import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useState } from "react";
import {
  AdditiveBlending,
  type BufferGeometry,
  Color,
  FrontSide,
  Matrix4,
  type Mesh,
  type Object3D,
  Quaternion,
  ShaderMaterial,
  Vector3,
} from "three";

/** Base color of the highlight glow (cyan-blue). */
const HIGHLIGHT_COLOR = 0x66bbff;

/**
 * Fresnel rim glow: brightest where the surface faces away from the camera, so
 * the part gets a soft glowing silhouette that follows its real shape instead of
 * a boxy outline. A small base term keeps the whole surface faintly lit.
 */
const VERTEX_SHADER = /* glsl */ `
  varying vec3 vNormal;
  varying vec3 vView;
  void main() {
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vNormal = normalize(normalMatrix * normal);
    vView = normalize(-mvPosition.xyz);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const FRAGMENT_SHADER = /* glsl */ `
  uniform vec3 uColor;
  uniform float uPower;
  uniform float uOpacity;
  uniform float uBase;
  varying vec3 vNormal;
  varying vec3 vView;
  void main() {
    float rim = pow(1.0 - max(dot(vNormal, vView), 0.0), uPower);
    float alpha = (rim + uBase) * uOpacity;
    gl_FragColor = vec4(uColor, alpha);
  }
`;

type MeshClone = {
  key: string;
  geometry: BufferGeometry;
  position: [number, number, number];
  quaternion: [number, number, number, number];
  scale: [number, number, number];
};

type GlowFit = {
  /** Local transform of the part group, mirrored onto the highlight group. */
  root: {
    position: [number, number, number];
    quaternion: [number, number, number, number];
    scale: [number, number, number];
  };
  meshes: MeshClone[];
};

/** Props for {@link DragHighlightGlow}. */
export interface DragHighlightGlowProps {
  /** The draggable part group to wrap with a glow. */
  target: Object3D | null;
  /** When true the glow gently pulses (the "armed" discovery state). */
  pulse?: boolean;
  /** A value that changes whenever the target moves, to recompute the clones. */
  refitKey?: unknown;
}

/**
 * Builds lightweight render-only clones of every mesh under `target`, reusing
 * their geometries (no buffer copies). The clones are placed in a sibling group
 * that mirrors the part group's local transform, so the glow lines up exactly
 * with the part while living in the parent frame (correct under translated/
 * rotated ancestors like the floor aligner).
 */
function collectMeshClones(target: Object3D): GlowFit | null {
  target.updateWorldMatrix(true, true);

  const rootInverse = new Matrix4().copy(target.matrixWorld).invert();
  const tmpMatrix = new Matrix4();
  const tmpPos = new Vector3();
  const tmpQuat = new Quaternion();
  const tmpScale = new Vector3();

  const meshes: MeshClone[] = [];
  target.traverse((obj) => {
    const mesh = obj as Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    if (mesh.userData?.isDragHelper) return;

    // Transform of this mesh relative to the part group.
    tmpMatrix.multiplyMatrices(rootInverse, mesh.matrixWorld);
    tmpMatrix.decompose(tmpPos, tmpQuat, tmpScale);
    meshes.push({
      key: `${mesh.geometry.uuid}-${meshes.length}`,
      geometry: mesh.geometry,
      position: [tmpPos.x, tmpPos.y, tmpPos.z],
      quaternion: [tmpQuat.x, tmpQuat.y, tmpQuat.z, tmpQuat.w],
      scale: [tmpScale.x, tmpScale.y, tmpScale.z],
    });
  });

  if (meshes.length === 0) return null;

  return {
    root: {
      position: [target.position.x, target.position.y, target.position.z],
      quaternion: [
        target.quaternion.x,
        target.quaternion.y,
        target.quaternion.z,
        target.quaternion.w,
      ],
      scale: [target.scale.x, target.scale.y, target.scale.z],
    },
    meshes,
  };
}

/**
 * Renders a soft glowing aura around a draggable part to signal it can be moved.
 * Follows the part's actual silhouette via a fresnel rim shader (no wireframe
 * box). The mesh-clone rebuild runs only when the target/position changes; the
 * per-frame cost is a single uniform update for the pulse.
 */
export const DragHighlightGlow = ({
  target,
  pulse = false,
  refitKey,
}: DragHighlightGlowProps) => {
  const [fit, setFit] = useState<GlowFit | null>(null);

  const material = useMemo(() => {
    return new ShaderMaterial({
      uniforms: {
        uColor: { value: new Color(HIGHLIGHT_COLOR) },
        uPower: { value: 2.5 },
        uOpacity: { value: pulse ? 0.6 : 0.9 },
        uBase: { value: 0.12 },
      },
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
      side: FrontSide,
    });
  }, [pulse]);

  useEffect(() => {
    void refitKey;
    setFit(target ? collectMeshClones(target) : null);
  }, [target, refitKey]);

  useEffect(() => {
    return () => material.dispose();
  }, [material]);

  useFrame((state) => {
    const opacity = material.uniforms.uOpacity;
    if (!opacity) return;
    opacity.value = pulse
      ? 0.45 + 0.35 * (0.5 + 0.5 * Math.sin(state.clock.elapsedTime * 4))
      : 0.9;
  });

  if (!fit) return null;

  return (
    <group
      position={fit.root.position}
      quaternion={fit.root.quaternion}
      scale={fit.root.scale}
      userData={{ isDragHelper: true }}
    >
      {fit.meshes.map((clone) => (
        <mesh
          key={clone.key}
          geometry={clone.geometry}
          material={material}
          position={clone.position}
          quaternion={clone.quaternion}
          scale={clone.scale}
          renderOrder={998}
          raycast={() => null}
          userData={{ isDragHelper: true }}
        />
      ))}
    </group>
  );
};

export default DragHighlightGlow;
